import type { ReactNode } from 'react'

/**
 * A labelled band of cards on a long page.
 *
 * The dashboard had seven card-rows stacked with nothing between them, so the only grouping was
 * adjacency — you had to already know that "Open findings" belonged with the risk lists and not
 * with the trend charts above it. Every card carries its own kicker and title, which means the
 * page had plenty of labels and no structure: nine headings all at the same rank.
 *
 * So this is the rank above a card, and it is deliberately quiet — a heading in the display face
 * and a hairline running to the margin, rather than another accent-coloured kicker competing with
 * the ones inside the cards it contains.
 *
 * Heading levels are the point, not decoration: the section is the `h2` and the cards within it
 * are `h3`, so the document outline matches what the page looks like. A screen reader can then
 * skim four sections instead of nine peer headings.
 */
export function PageSection({
  title,
  note,
  children,
}: {
  title: string
  /** An optional right-aligned aside — a window, a count, a scope caveat. */
  note?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="page-section" aria-label={title}>
      <div className="page-section-head">
        <h2>{title}</h2>
        <span className="page-section-rule" aria-hidden="true" />
        {note && <span className="page-section-note">{note}</span>}
      </div>
      {children}
    </section>
  )
}
